import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "../../App";
import { useAdminAuth } from "./AdminLayout";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { toast } from "sonner";
import { 
  Music, 
  Save, 
  RotateCcw, 
  Plus,
  Trash2,
  Zap,
  Moon,
  Flame,
  Coffee,
  Target,
  GripVertical
} from "lucide-react";

const iconOptions = [
  { id: "Zap", name: "Zap (Energy)", Icon: Zap },
  { id: "Moon", name: "Moon (Calm)", Icon: Moon },
  { id: "Flame", name: "Flame (Intense)", Icon: Flame },
  { id: "Coffee", name: "Coffee (Chill)", Icon: Coffee },
  { id: "Target", name: "Target (Focus)", Icon: Target },
  { id: "Music", name: "Music (General)", Icon: Music },
];

const colorOptions = [
  { id: "#FFD700", name: "Gold" },
  { id: "#9370DB", name: "Purple" },
  { id: "#FF4500", name: "Orange Red" },
  { id: "#20B2AA", name: "Teal" },
  { id: "#4169E1", name: "Royal Blue" },
  { id: "#32CD32", name: "Lime Green" },
  { id: "#FF69B4", name: "Hot Pink" },
];

export default function AdminVibes() {
  const { token } = useAdminAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [vibes, setVibes] = useState([]);

  useEffect(() => {
    fetchVibes();
  }, []);

  const fetchVibes = async () => {
    try {
      const response = await axios.get(`${API}/admin/vibes`, {
        
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      setVibes(response.data.vibes || []);
    } catch (error) {
      console.error("Failed to fetch vibes", error);
      toast.error("Failed to load vibes");
    } finally {
      setLoading(false);
    }
  };

  const handleVibeChange = (index, field, value) => {
    setVibes(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  const addVibe = () => {
    const newId = `vibe_${Date.now()}`;
    setVibes(prev => [...prev, {
      id: newId,
      name: "New Vibe",
      description: "Description here",
      icon: "Music",
      color: "#c9a227"
    }]);
  };

  const removeVibe = (index) => {
    if (vibes.length <= 1) {
      toast.error("You must have at least one vibe");
      return;
    }
    setVibes(prev => prev.filter((_, i) => i !== index));
  };

  const handleSave = async () => {
    // Validate
    for (const vibe of vibes) {
      if (!vibe.id || !vibe.name) {
        toast.error("All vibes must have an ID and name");
        return;
      }
    }

    setSaving(true);
    try {
      await axios.put(`${API}/admin/vibes`, { vibes }, {
        
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      toast.success("Vibes saved successfully!");
    } catch (error) {
      console.error("Failed to save vibes", error);
      toast.error("Failed to save vibes");
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (!window.confirm("Reset vibes to defaults? This cannot be undone.")) return;
    
    setLoading(true);
    try {
      const response = await axios.post(`${API}/admin/vibes/reset`, {}, {
        
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      setVibes(response.data.vibes);
      toast.success("Vibes reset to defaults");
    } catch (error) {
      toast.error("Failed to reset vibes");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <h1 className="font-serif text-2xl font-bold">Audio Vibes</h1>
        <div className="space-y-4">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="h-24 bg-muted rounded-lg animate-pulse" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-vibes">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-3">
          <Music className="h-8 w-8 text-gold" />
          <div>
            <h1 className="font-serif text-2xl font-bold">Audio Vibes</h1>
            <p className="text-sm text-muted-foreground">Manage audio vibe categories for content</p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={handleReset}>
            <RotateCcw className="h-4 w-4 mr-2" />
            Reset
          </Button>
          <Button onClick={handleSave} disabled={saving}>
            <Save className="h-4 w-4 mr-2" />
            {saving ? "Saving..." : "Save Changes"}
          </Button>
        </div>
      </div>

      {/* Vibes List */}
      <div className="space-y-4">
        {vibes.map((vibe, index) => {
          const IconComponent = iconOptions.find(i => i.id === vibe.icon)?.Icon || Music;
          return (
            <Card key={vibe.id} className="bg-card border-border">
              <CardContent className="pt-6">
                <div className="flex items-start gap-4">
                  {/* Preview */}
                  <div 
                    className="w-16 h-16 rounded-full flex items-center justify-center flex-shrink-0"
                    style={{ backgroundColor: `${vibe.color}20` }}
                  >
                    <IconComponent className="h-8 w-8" style={{ color: vibe.color }} />
                  </div>
                  
                  {/* Fields */}
                  <div className="flex-1 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    <div className="space-y-2">
                      <Label>ID (unique)</Label>
                      <Input
                        value={vibe.id}
                        onChange={(e) => handleVibeChange(index, "id", e.target.value.toLowerCase().replace(/\s/g, '_'))}
                        placeholder="vibe_id"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Name</Label>
                      <Input
                        value={vibe.name}
                        onChange={(e) => handleVibeChange(index, "name", e.target.value)}
                        placeholder="Vibe Name"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Description</Label>
                      <Input
                        value={vibe.description}
                        onChange={(e) => handleVibeChange(index, "description", e.target.value)}
                        placeholder="Short description"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <div className="space-y-2">
                        <Label>Icon</Label>
                        <select
                          value={vibe.icon}
                          onChange={(e) => handleVibeChange(index, "icon", e.target.value)}
                          className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm"
                        >
                          {iconOptions.map(opt => (
                            <option key={opt.id} value={opt.id}>{opt.name}</option>
                          ))}
                        </select>
                      </div>
                      <div className="space-y-2">
                        <Label>Color</Label>
                        <div className="flex items-center gap-2">
                          <input
                            type="color"
                            value={vibe.color}
                            onChange={(e) => handleVibeChange(index, "color", e.target.value)}
                            className="w-10 h-10 rounded cursor-pointer"
                          />
                          <Input
                            value={vibe.color}
                            onChange={(e) => handleVibeChange(index, "color", e.target.value)}
                            className="flex-1"
                            placeholder="#000000"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  {/* Delete */}
                  <Button
                    variant="ghost"
                    size="icon"
                    className="text-muted-foreground hover:text-red-500"
                    onClick={() => removeVibe(index)}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Add Button */}
      <Button variant="outline" onClick={addVibe} className="w-full border-dashed">
        <Plus className="h-4 w-4 mr-2" />
        Add New Vibe
      </Button>
    </div>
  );
}
